import React, {
  useContext,
  useState,
  createContext,
  ReactNode,
  useEffect,
  ChangeEvent
} from 'react';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useCreateTaskState } from './CreateTask';
import { PreCreatedTask } from '../sidebar/views/CreateTask';
import { useAnalyticsState } from './Analytics';
import { SupportedLocales } from '../public_feedback/views/strings';
import { Severity } from '../sidebar/views/CreateTask/create_task/types';
import useConfigDefaultsInCreateTask from '../sidebar/views/hooks/useConfigDefaults';
import { Tag } from '../sidebar/models/Project';
import { Tag as TagType } from '../sidebar/views/CreateTask/create_task/types';

// @ts-expect-error
const PublicFeedbackStateContext = createContext();

type FeedbackState =
  | 'TAB'
  | 'TYPE'
  | 'TASK'
  | 'SELECTOR'
  | 'RECORDING'
  | 'ERROR'
  | 'LOADING'
  | 'SUCCESS'
  | 'UPLOADING';

type Tags = { tag_ids?: number[]; tag_names?: string[] };

const PublicFeedbackProvider = ({ children }: { children: ReactNode }) => {
  const { analytics } = useAnalyticsState();
  const {
    createTask,
    tagInfo,
    recording,
    initialiseTask,
    type,
    clearTaskData,
    project
  } = useCreateTaskState();

  const [feedbackState, setFeedbackState] = useState<FeedbackState>('TAB');
  const [description, setDescription] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [privacyChecked, setPrivacyChecked] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isDragging, setisDragging] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [taskSubmitted, setTaskSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const [severity, setSeverity] = useState<Severity>(0);
  const [tags, setTags] = useState<Tags>({ tag_ids: [], tag_names: [] });
  const [hidingTab, setHidingTab] = useState<boolean>(false);

  const availableTags = project.tags.reduce((result: Tag[], value: Tag) => {
    if (value.active) {
      value.display = value.name;
      result.push(value);
    }
    return result;
  }, []) as TagType[];

  const onTagsChange = (tags: TagType[]) =>
    setTags({
      tag_ids: tags.map(({ id }) => id),
      tag_names: tags.map(({ display }) => display)
    });

  const onSeverityChange = (severity: Severity) => setSeverity(severity);

  useConfigDefaultsInCreateTask({
    availableTags,
    onTagsChange,
    onSeverityChange
  });

  const [
    containerElement,
    setContainerElement
  ] = useState<HTMLDivElement | null>(null);

  const onModalClose = () => {
    setFeedbackState('TAB');
    setTimeout(() => setHidingTab(false), 300);
  };
  const onDragStart = () => setisDragging(true);
  const onDragStop = () => setisDragging(false);
  const onSuccess = () => {
    setFeedbackState('SUCCESS');
    if (!taskSubmitted) setTaskSubmitted(true);
    setSubmitting(false);
  };
  const onLoading = () => setFeedbackState('LOADING');
  const onUploading = () => setFeedbackState('UPLOADING');

  const cancel = () => {
    onModalClose();
    clearTaskData();
    setDescription('');
    if (!taskSubmitted) setEmail('');
    setPrivacyChecked(false);
    setUploadProgress(null);
  };

  const onCancel = (event: KeyboardEvent) => cancel();

  const onProgress = (
    loaded: number,
    total: number,
    lengthComputable: boolean
  ) => {
    if (lengthComputable) setUploadProgress(Math.round((loaded / total) * 100));
  };

  const onError = (err: any) => {
    setFeedbackState('ERROR');
    setError(err);
  };

  const onDescriptionChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setDescription(event.target.value);
  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) =>
    setEmail(event.currentTarget.value);
  const onCheckboxChange = (event: CheckboxChangeEvent) =>
    setPrivacyChecked(event.target.checked);

  const locale = window.BugHerdConfig?.locale || 'en';

  const { feedback, reporter } = window.BugHerdConfig || {};

  const hiddenTab =
    feedback?.hidden === 'true' ||
    feedback?.hidden === true ||
    feedback?.hide === 'true' ||
    feedback?.hide === true;

  const useLightBackground =
    feedback?.use_light_background === 'true' ||
    feedback?.use_light_background === true;

  const customFeedbackId = feedback?.custom_feedback_id;

  const tabPosition = feedback?.tab_position;

  const reporterEmail = reporter?.email;
  const emailRequired =
    reporter?.required === 'true' || reporter?.required === true;

  const isValidEmail = () =>
    /([\w\.%\+\-]+)@([\w\-]+\.)+([\w]{2,})/i.test(reporterEmail || email);

  const submitEnabled =
    (isValidEmail() || !emailRequired) && privacyChecked && !submitting;

  const hideFader = ['TAB', 'SELECTOR', 'RECORDING'].includes(feedbackState);

  const submitTask = () => {
    setSubmitting(true);
    createTask(
      // @ts-expect-error
      {
        assignee_ids: [],
        attachments: null,
        closed_at: null,
        column_id: null,
        description,
        requester_email: reporterEmail || email,
        tag_ids: tags?.tag_ids,
        tag_names: tags?.tag_names,
        priority_id: severity
      } as PreCreatedTask,
      onSuccess,
      onLoading,
      onError,
      onUploading,
      onProgress
    );
  };

  useEffect(() => {
    if (uploadProgress === 100) {
      setFeedbackState('SUCCESS');
    }
  }, [uploadProgress]);

  useEffect(() => {
    if (type) {
      analytics.track('Create task started');
      initialiseTask(onCancel);
    }
  }, [type]);

  useEffect(() => {
    const tagInfoHasKeys = !!Object.keys(tagInfo).length;
    if (tagInfoHasKeys || recording) {
      setFeedbackState('TASK');
    }
  }, [tagInfo, recording]);

  return (
    <PublicFeedbackStateContext.Provider
      value={{
        feedbackState,
        setFeedbackState,
        containerElement,
        setContainerElement,
        onDragStart,
        onDragStop,
        isDragging,
        description,
        onDescriptionChange,
        email,
        onEmailChange,
        privacyChecked,
        onCheckboxChange,
        submitEnabled,
        submitTask,
        submitting,
        onModalClose,
        locale,
        cancel,
        uploadProgress,
        error,
        reporterEmail,
        hiddenTab,
        useLightBackground,
        tabPosition,
        customFeedbackId,
        setHidingTab,
        hidingTab,
        hideFader
      }}
    >
      {children}
    </PublicFeedbackStateContext.Provider>
  );
};

type PublicFeedbackState = {
  feedbackState: FeedbackState;
  setFeedbackState: (state: FeedbackState) => void;
  containerElement: HTMLDivElement;
  setContainerElement: (ref: HTMLDivElement) => void;
  onDragStart: () => void;
  onDragStop: () => void;
  isDragging: boolean;
  description: string;
  onDescriptionChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  email: string;
  onEmailChange: (event: ChangeEvent<HTMLInputElement>) => void;
  privacyChecked: boolean;
  onCheckboxChange: (event: CheckboxChangeEvent) => void;
  submitEnabled: boolean;
  submitTask: () => void;
  submitting: boolean;
  onModalClose: () => void;
  locale: SupportedLocales;
  cancel: () => void;
  uploadProgress: null | number;
  error: any;
  reporterEmail?: string;
  hiddenTab: boolean;
  useLightBackground: boolean;
  tabPosition?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  customFeedbackId?: string;
  setHidingTab: (hidintTab: boolean) => void;
  hidingTab: boolean;
  hideFader: boolean;
};

const usePublicFeedbackState = (): PublicFeedbackState => {
  const context = useContext(PublicFeedbackStateContext);

  if (context === undefined) {
    throw new Error(
      'usePublicFeedbackState must be used within a PublicFeedbackProvider'
    );
  }

  // @ts-expect-error
  return context;
};

export { PublicFeedbackProvider, usePublicFeedbackState };
